import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

interface AuthState {
  user: { id: number; email: string; is_admin: boolean } | null;
  status: 'loading' | 'done';
  error: string | null;
}

const initialState: AuthState = { user: null, status: 'loading', error: null };

export const fetchCurrentUser = createAsyncThunk('auth/fetchCurrentUser', async () => {
  const res = await fetch('/api/me', { headers: { 'Accept': 'application/json' } });
  if (!res.ok) return null;
  return await res.json() as { id: number; email: string; is_admin: boolean };
});

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    const res = await fetch('/api/session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '',
      },
      body: JSON.stringify({ email_address: email, password }),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error as string);
    return data as { id: number; email: string; is_admin: boolean };
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await fetch('/api/session', {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '',
    },
  });
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = 'done';
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.user = null;
        state.status = 'done';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
      });
  },
});

export default authSlice.reducer;
